import file from '../../common/file';
import Viewer from '../Viewer/PopupViewer';
import React, { useState, useEffect, useContext, useRef } from 'react';
import folder from '../../common/folder';
import { useHistory } from 'react-router-dom';
import File from './File';
import Folder from './Folder';
import { download } from '../../api/File';
import LoadingSkeleton from './LoadingSkeleton';
import apiUrl from '../../assets/apiUrl';
import sortDates from '../../assets/sortDates';
import { getNestedFolders } from '../../assets/fileTree';
import MoveTo from './MoveTo';
import AuthContext, { getAccessToken } from '../Auth';
import PropertiesViewer from '../PropertiesViewer';
import formatDate from '../../assets/formatDate';
import formatBytes from '../../assets/formatBytes';
import { FileSystemContext } from '../FileSystem';
import buildFilePath from '../../assets/buildFilePath';

interface Props {
  activeFolder: string | null;
}

const FileTree: React.FC<Props> = ({ activeFolder }) => {
  const [token, setToken] = useState<string>('');
  const { fetchWrapper } = useContext(AuthContext);
  const { getSignedUrl } = useContext(FileSystemContext).useFilesMethods();
  const { isLoading, useFiles, useFolders } = useContext(FileSystemContext);
  const [allFiles, setAllFiles] = useFiles();
  const [allFolders, setAllFolders] = useFolders();
  useEffect(() => {
    async function getToken() {
      setToken(await getAccessToken());
    }
    getToken();
  }, []);
  const [isImageViewerOpen, setIsImageViewerOpen] = useState(false);
  const [viewerFile, setViewerFile] = useState<file>();
  const viewerDisplay = (file: file) => {
    setViewerFile(file);
    setIsImageViewerOpen(true);
  };
  const history = useHistory();
  const openFolder = (folder_uid: string) => {
    history.push(folder_uid);
  };
  const [folderName, setFoldername] = useState<string>('');
  const [folderActive, setFolderActive] = useState<boolean>(false);
  function handleChange(e: any) {
    setFoldername(e.target.value);
  }
  async function handleSubmit(e: any) {
    e.preventDefault();
    let formData = new FormData();
    formData.append('title', folderName);
    const currentFolder = window.location.href.substring(
      window.location.href.lastIndexOf('/') + 1
    );
    currentFolder !== '' &&
      'protonfile' &&
      formData.append('parent_folder', currentFolder);

    let queryString = `?title=${encodeURIComponent(folderName)}`;
    if (currentFolder !== '' && currentFolder !== 'protonfile') {
      queryString += `&parent_folder=${encodeURIComponent(currentFolder)}`;
    }

    const res = await (
      await fetchWrapper(`${apiUrl}/folder/create${queryString}`, {
        method: 'POST',
        headers: { 'x-access-token': token },
      })
    ).json();
    setAllFolders((state) =>
      !state.find((e) => e.folder_uid === res.folder_uid)
        ? [...state, res]
        : state
    );
    setFoldername('');
    setFolderActive(false);
  }
  function dynamicSort(property: string, sortOrder: number) {
    return function (a: any, b: any) {
      /* next line works with strings and numbers,
       * and you may want to customize it to your needs
       */
      const result =
        a[property] < b[property] ? -1 : a[property] > b[property] ? 1 : 0;
      return result * sortOrder;
    };
  }
  const findSize = (folder: folder) => {
    const nestedFolders = getNestedFolders(folder.folder_uid, allFolders).map(
      (e) => e.folder_uid
    );
    nestedFolders.push(folder.folder_uid);
    return allFiles
      .filter((e) => nestedFolders.includes(e.folder_uid!))
      .map((e) => parseInt(e.size))
      .reduce((a, b) => a + b, 0);
  };
  const findLastUpdated = (folder: folder) => {
    const nestedFolders = getNestedFolders(folder.folder_uid, allFolders).map(
      (e) => e.folder_uid
    );
    nestedFolders.push(folder.folder_uid);
    const dates = allFiles
      .filter((e) => nestedFolders.includes(e.folder_uid!))
      .map((e) => Math.floor(new Date(e.last_updated).getTime() / 1000));
    const latestDate = Math.max.apply(null, dates);
    return new Date(latestDate * 1000);
  };
  function sortFiles(arr: file[], prop: string, sortOrder: number): file[] {
    switch (prop) {
      case 'name':
        return arr.sort(dynamicSort('filename', sortOrder));
      case 'size':
        return arr
          .map((e) => {
            return { ...e, size: parseInt(e.size) };
          })
          .sort(dynamicSort('size', sortOrder))
          .map((e) => {
            return { ...e, size: e.size.toString() };
          });
      case 'last_updated':
        return arr.sort(dynamicSort('last_updated', sortOrder));
      default:
        return arr.sort(dynamicSort('filename', sortOrder));
    }
  }
  function sortFolders(
    arr: folder[],
    prop: string,
    sortOrder: number
  ): folder[] {
    switch (prop) {
      case 'name':
        return arr.sort(dynamicSort('title', sortOrder));
      case 'size':
        return arr
          .map((folder) => {
            return { ...folder, total_size: findSize(folder) };
          })
          .sort(dynamicSort('total_size', sortOrder));
      case 'last_updated':
        return arr
          .map((a) => {
            return {
              ...a,
              last_updated: findLastUpdated(a),
            };
          })
          .sort(dynamicSort('last_updated', sortOrder));
      default:
        return arr.sort(dynamicSort('title', sortOrder));
    }
  }
  function addSortPreference(name: string) {
    const item = localStorage.getItem('sort-preference')?.split(';');

    if (item === undefined) {
      localStorage.setItem('sort-preference', `${name};1`);
      return;
    }

    if (item[0] === name) {
      localStorage.setItem(
        'sort-preference',
        `${item[0]};${item[1] === '1' ? '-1' : '1'}`
      );
    } else {
      localStorage.setItem('sort-preference', `${name};1`);
    }

    setSortPreference(localStorage.getItem('sort-preference'));
  }
  const [sortPreference, setSortPreference] = useState(
    localStorage.getItem('sort-preference')
  );
  function parseSortPreference(str: string | null): [string, number] {
    if (str === null) {
      return ['size', 1];
    } else {
      return [str.split(';')[0], parseInt(str.split(';')[1])];
    }
  }
  const [moveToFolder, setMoveToFolder] = useState<string | undefined>();
  const [isMoveOpen, setIsMoveOpen] = useState(false);
  const [isFile, setIsFile] = useState(false);
  const [propertiesViewerProps, setPropertiesViewerProps] = useState<any>([]);
  const [propertiesViewerIsOpen, setPropertiesViewerIsOpen] = useState(false);
  useEffect(() => {
    if (!isImageViewerOpen && viewerFile)
      window.Protonfile.FileViewer.emit('close', viewerFile);
  }, [isImageViewerOpen]);
  return (
    <table>
      <Viewer
        isOpen={isImageViewerOpen}
        setIsOpen={setIsImageViewerOpen}
        file={viewerFile}
        onEnd={() => {}}
      />
      <MoveTo
        id={moveToFolder}
        open={isMoveOpen}
        onClose={() => setIsMoveOpen(false)}
        isFile={isFile}
      />
      <PropertiesViewer
        properties={propertiesViewerProps}
        isOpen={propertiesViewerIsOpen}
        onClose={() => setPropertiesViewerIsOpen(false)}
      />
      <tbody>
        <tr>
          <th onClick={() => addSortPreference('name')}>
            <p>
              Name
              {parseSortPreference(sortPreference)[0] === 'name' && (
                <i
                  className={
                    parseSortPreference(sortPreference)[1] === 1
                      ? `fas fa-caret-up sort-icon`
                      : `fas fa-caret-down sort-icon`
                  }
                />
              )}
            </p>
          </th>
          <th onClick={() => addSortPreference('size')}>
            <p>
              Size
              {parseSortPreference(sortPreference)[0] === 'size' && (
                <i
                  className={
                    parseSortPreference(sortPreference)[1] === 1
                      ? `fas fa-caret-up sort-icon`
                      : `fas fa-caret-down sort-icon`
                  }
                />
              )}
            </p>
          </th>
          <th
            onClick={() => addSortPreference('last_updated')}
            className="last-updated"
          >
            <p>
              Last updated
              {parseSortPreference(sortPreference)[0] === 'last_updated' && (
                <i
                  className={
                    parseSortPreference(sortPreference)[1] === 1
                      ? `fas fa-caret-up sort-icon`
                      : `fas fa-caret-down sort-icon`
                  }
                />
              )}
            </p>
          </th>
          <th></th>
        </tr>
        <tr className="mkdir">
          <td>
            <form onSubmit={handleSubmit}>
              <label htmlFor="name">
                <i
                  className="fas fa-folder-plus"
                  onClick={() => setFolderActive(!folderActive)}
                ></i>
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={folderName}
                className={!folderActive ? 'hidden' : ''}
                onChange={handleChange}
              ></input>
            </form>
          </td>
        </tr>
        {isLoading &&
          [...Array(10)].map((_e, index) => <LoadingSkeleton key={index} />)}
        {sortFolders(
          allFolders.filter((e) => e.parent_folder === activeFolder),
          parseSortPreference(sortPreference)[0],
          parseSortPreference(sortPreference)[1]
        ).map((folder) => (
          <Folder
            key={folder.folder_uid}
            size={allFiles
              .filter((e) => e.folder_uid === folder.folder_uid)
              .map((e) => parseInt(e.size))
              .reduce((a, b) => a + b, 0)}
            folder={folder}
            onOpen={openFolder}
            onDelete={(folder_uid) => {
              setAllFolders((state) =>
                state.filter((e) => e.folder_uid !== folder_uid)
              );
            }}
            lastUpdated={sortDates(
              allFiles
                .filter((e) => e.folder_uid === folder.folder_uid)
                .map((e) => e.last_updated)
            )}
            zipFolder={async (folder_uid: string) => {
              const res = await (
                await fetchWrapper(`${apiUrl}/zip?folder_uid=${folder_uid}`)
              ).json();
              download(
                `${apiUrl}/zip/${res.signed_token}`,
                `${folder.title}.zip`,
                0,
                { auth: true, token: await getAccessToken() }
              );
            }}
            allFolders={allFolders}
            allFiles={allFiles}
            onMove={(folder_uid) => {
              setMoveToFolder(folder_uid);
              setIsFile(false);
              setIsMoveOpen(true);
            }}
            openPropertiesViewer={() => {
              setPropertiesViewerProps([
                [
                  'Title',
                  folder.title,
                  async (value: any) => {
                    await fetchWrapper(
                      `${apiUrl}/rename/${folder.folder_uid}?name=${value}`,
                      { method: 'PUT' }
                    );
                    setAllFolders((state) => {
                      return state.map((e) =>
                        e.folder_uid === folder.folder_uid
                          ? Object.assign({}, e, {
                              title: value,
                            })
                          : e
                      );
                    });
                    return true;
                  },
                ],
                [
                  'Path',
                  buildFilePath(folder, allFolders, {
                    includeTargetName: false,
                  }),
                ],
              ]);
              setPropertiesViewerIsOpen(true);
            }}
          />
        ))}
        {sortFiles(
          allFiles.filter((e) => e.folder_uid === activeFolder),
          parseSortPreference(sortPreference)[0],
          parseSortPreference(sortPreference)[1]
        ).map((file) => (
          <File
            key={file.file_uid}
            file={file}
            download={async (
              file_uid: string,
              filename: string,
              size: number
            ) => {
              download(
                await getSignedUrl(`${apiUrl}/file?file_uid=${file_uid}`),
                filename,
                size,
                { auth: true, token: token }
              );
            }}
            onDisplay={viewerDisplay}
            onMove={(file_uid) => {
              setMoveToFolder(file_uid);
              setIsFile(true);
              setIsMoveOpen(true);
            }}
            openPropertiesViewer={() => {
              setPropertiesViewerProps([
                [
                  'Filename',
                  file.filename,
                  async (value: any) => {
                    await fetchWrapper(
                      `${apiUrl}/rename/${file.file_uid}?name=${value}`,
                      { method: 'PUT' }
                    );
                    setAllFiles((state) => {
                      return state.map((e) =>
                        e.file_uid === file.file_uid
                          ? Object.assign({}, e, {
                              filename: value,
                            })
                          : e
                      );
                    });
                    return true;
                  },
                ],
                ['_divider'],
                ['Last change', formatDate(file.last_updated)],
                ['Size', formatBytes(parseInt(file.size))],
                [
                  'Path',
                  buildFilePath(file, allFolders, { includeTargetName: false }),
                ],
              ]);
              setPropertiesViewerIsOpen(true);
            }}
          />
        ))}
      </tbody>
    </table>
  );
};

export default FileTree;
